import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Calendar, RefreshCw, Globe, MapPin, Settings, BookOpen, Award } from 'lucide-react'
import PageHeader from '@shared/components/PageHeader'
import SEO from '@shared/components/SEO'


const FACTS = [
    { icon: Calendar, label: 'Established', value: '2019' },
    { icon: RefreshCw, label: 'Frequency', value: 'Biannual' },
    { icon: Globe, label: 'Mode', value: 'Online & Print' },
    { icon: MapPin, label: 'Location', value: 'Delhi' },
    { icon: Settings, label: 'Platform', value: 'OJS' }
]

const PARTICULARS: [string, string][] = [
    ['Journal title', 'Indian Studies Review'],
    ['P-ISSN (Print)', '2583-004X'],
    ['E-ISSN (Online)', '2582-7154'],
    ['Frequency', 'Bi-annual'],
    ['Publisher', 'Centre for Study of Politics & Governance'],
    ['Chief Editor', 'Prof. Sangit Kumar Ragi'],
    ['Subject', 'Social Sciences (Multidisciplinary)'],
    ['Language', 'English']
]

export default function AboutJournal() {
    return (
        <>
            <SEO
                title="About the Journal — Indian Studies Review"
                description="Indian Studies Review (ISR) is a peer-reviewed, biannual social-sciences journal published from Delhi by the Centre for Study of Politics and Governance, previously listed in the UGC CARE List."
                path="/about-the-journal"
            />
            <PageHeader
                eyebrow="The Journal"
                title="Indian Studies Review"
                lede="A peer-reviewed social-sciences journal advancing rigorous research and scholarly discourse — published biannually from Delhi"
            />

            <section className="section">
                <div className="container-prose grid lg:grid-cols-[1fr_19rem] gap-12 lg:gap-16 items-start">
                    <div>
                        <motion.div
                            initial={{ opacity: 0, y: 14 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.55 }}
                            className="prose-body dropcap dropcap--accent"
                        >
                            <p>
                                <strong>Indian Studies Review (ISR)</strong> is a prestigious
                                academic journal published by the Centre for Study of Politics
                                and Governance. The journal covers a wide range of areas within
                                the social sciences — including sociology, anthropology,
                                economics, political science, history and related
                                interdisciplinary fields.
                            </p>
                            <p>
                                Since its establishment in 2019, the journal has been committed
                                to promoting high-quality research and scholarly discourse. ISR
                                is published biannually (January and July), both online and in
                                print, from Delhi.
                            </p>
                            <p>
                                The journal uses the Open Journal System (OJS) to manage
                                manuscript submissions, peer review, editorial workflow and
                                production — ensuring transparency, efficiency and adherence to
                                global publishing standards.
                            </p>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5 }}
                            className="mt-9 grid grid-cols-2 sm:grid-cols-3 gap-px"
                            style={{ background: 'var(--color-rule)', border: '1px solid var(--color-rule)' }}
                        >
                            {FACTS.map((f) => {
                                const Icon = f.icon
                                return (
                                    <div
                                        key={f.label}
                                        className="px-4 py-4 flex items-center gap-3"
                                        style={{ background: 'var(--color-paper-50)' }}
                                    >
                                        <Icon size={18} style={{ color: 'var(--color-gold-700)', flexShrink: 0 }} />
                                        <div>
                                            <div
                                                className="text-[10px] uppercase font-semibold"
                                                style={{ letterSpacing: '0.14em', color: 'var(--color-ink-400)' }}
                                            >
                                                {f.label}
                                            </div>
                                            <div
                                                className="font-display text-[15px]"
                                                style={{ color: 'var(--color-ink-700)' }}
                                            >
                                                {f.value}
                                            </div>
                                        </div>
                                    </div>
                                )
                            })}
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5 }}
                            className="mt-10 p-6 md:p-7 flex gap-5"
                            style={{
                                background: 'var(--color-paper-100)',
                                borderLeft: '3px solid var(--color-gold-500)',
                                borderRadius: '2px'
                            }}
                        >
                            <Award size={26} style={{ color: 'var(--color-gold-700)', flexShrink: 0, marginTop: 2 }} />
                            <div>
                                <h3
                                    className="font-display"
                                    style={{ fontSize: '1.15rem', color: 'var(--color-ink-700)' }}
                                >
                                    UGC CARE recognition
                                </h3>
                                <p className="mt-2 text-[15px] leading-relaxed" style={{ color: 'var(--color-ink-600)' }}>
                                    ISR was previously included in the UGC CARE List of journals
                                    and recognised by the University Grants Commission for its
                                    quality and research output — confirming that the journal meets
                                    high standards of academic rigour and serves as a reliable
                                    resource for academic evaluation and decision-making.
                                </p>
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5 }}
                            className="prose-body mt-9"
                        >
                            <p>
                                With its strong online presence, ISR reaches a wide audience
                                nationally and internationally. The journal accepts submissions
                                from authors across the globe and is supported by an experienced
                                editorial team of subject experts who ensure a rigorous and
                                unbiased peer-review process.
                            </p>
                        </motion.div>

                        <motion.blockquote
                            initial={{ opacity: 0, y: 12 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.55 }}
                            className="mt-10 p-7 font-display"
                            style={{
                                background: 'var(--color-gold-800)',
                                color: 'var(--color-paper-50)',
                                fontSize: '1.2rem',
                                lineHeight: 1.5,
                                letterSpacing: '-0.01em',
                                borderRadius: '2px'
                            }}
                        >
                            Indian Studies Review is a highly respected academic journal dedicated
                            to advancing knowledge in the social sciences — committed to research
                            excellence, ethical publishing and accessibility for the global
                            academic community.
                        </motion.blockquote>
                    </div>

                    <motion.aside
                        initial={{ opacity: 0, y: 14 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.55 }}
                        className="lg:sticky lg:top-24 lg:self-start"
                    >
                        <div
                            style={{
                                border: '1px solid var(--color-rule-strong)',
                                background: 'var(--color-paper-50)',
                                boxShadow: '0 14px 40px -22px rgba(13,27,42,0.18)'
                            }}
                        >
                            <div
                                className="px-5 py-4 flex items-center gap-2.5"
                                style={{ background: 'var(--color-gold-800)', color: 'var(--color-paper-50)' }}
                            >
                                <BookOpen size={16} />
                                <span
                                    className="text-[11px] uppercase font-semibold"
                                    style={{ letterSpacing: '0.18em' }}
                                >
                                    At a glance
                                </span>
                            </div>
                            <dl className="px-5 py-2">
                                {PARTICULARS.map(([k, v]) => (
                                    <div
                                        key={k}
                                        className="py-3"
                                        style={{ borderBottom: '1px solid var(--color-rule)' }}
                                    >
                                        <dt
                                            className="text-[10.5px] uppercase font-semibold"
                                            style={{ letterSpacing: '0.12em', color: 'var(--color-ink-400)' }}
                                        >
                                            {k}
                                        </dt>
                                        <dd
                                            className="mt-1 text-[14px]"
                                            style={{ color: 'var(--color-ink-700)' }}
                                        >
                                            {v}
                                        </dd>
                                    </div>
                                ))}
                            </dl>
                            <div className="px-5 pb-5 pt-2 flex flex-col gap-2">
                                <Link to="/journal-particulars" className="btn btn-ghost w-full text-center">
                                    Full particulars
                                </Link>
                                <Link to="/current-issue" className="btn btn-gold w-full text-center">
                                    Current issue →
                                </Link>
                            </div>
                        </div>

                        <div className="mt-5">
                            <div
                                className="text-[10.5px] uppercase font-semibold mb-1"
                                style={{ letterSpacing: '0.16em', color: 'var(--color-ink-400)' }}
                            >
                                Explore
                            </div>
                            {[
                                { to: '/editorial-board', label: 'Editorial Board' },
                                { to: '/editorial-workflow', label: 'Editorial Workflow' },
                                { to: '/guidelines', label: 'Submission Guidelines' },
                                { to: '/archives', label: 'Archives' }
                            ].map((l) => (
                                <Link
                                    key={l.to}
                                    to={l.to}
                                    className="flex items-center justify-between py-2.5 text-[13.5px] group"
                                    style={{ borderTop: '1px solid var(--color-rule)', color: 'var(--color-ink-600)' }}
                                >
                                    <span className="group-hover:text-[var(--color-gold-700)] transition-colors">
                                        {l.label}
                                    </span>
                                    <span style={{ color: 'var(--color-gold-700)' }}>→</span>
                                </Link>
                            ))}
                        </div>
                    </motion.aside>
                </div>
            </section>
        </>
    )
}
